import { DataMode, MappingFunnelDataSource } from "@src/util/ab-testing/funnel/maping-funnel-data-source";
import { Tag, Table, Form } from "antd";
import { ColumnsType } from "antd/es/table";
import { ApplyValueStateType } from ".";
import { FormInstance } from "antd/lib/form";
import React from "react";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { CellRenderWithSignificant } from "./cell-render";
import { uniq } from "lodash";
import { css } from "@emotion/react";
import { toTitle } from "@src/helpers/text-helper";
import { ColumnType } from "antd/lib/table";
import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";

const FunnelTableComponent: React.FC<{
    result: Record<string, any>[] | undefined;
    isLoading: boolean;
    applyValue: ApplyValueStateType;
    form: FormInstance;
    mappingFunnelDataSource: MappingFunnelDataSource;
    mode: DataMode;
    significantInfoAllStep: Record<string, Record<string, any>> | undefined;
    variants: string[];
    getExpAliasById: (expId: string) => string;
}> = ({
    applyValue,
    form,
    result,
    isLoading,
    mappingFunnelDataSource,
    mode,
    significantInfoAllStep,
    variants,
    getExpAliasById,
}) => {
    const significanceLevel = Form.useWatch(ChartFormFieldsEnum.FUNNEL_SIGNIFICANT, form);
    const { baseVariant } = useGetGeneralField({ form });

    const dataSource = React.useMemo(() => {
        if (!result || result?.length === 0) return [];
        mappingFunnelDataSource.setRawData(result);
        return mappingFunnelDataSource.generateDataSource(mode);
    }, [mappingFunnelDataSource, mode, result]);

    console.log("dataSource ", dataSource, "significantInfoAllStep ", significantInfoAllStep);
    const columns = (breakdownValue: string): ColumnsType<any> => {
        const _variants = uniq([baseVariant, ...variants].filter((f) => !!f));

        return [
            {
                title: "Index",
                dataIndex: "index",
                key: "index",
                width: 50,
                fixed: "left",
                render: (value: string) => {
                    return <Tag color="blue">{value + 1}</Tag>;
                },
            },
            {
                title: "Step",
                dataIndex: "step",
                key: "step",
                width: 200,
                fixed: "left",
                render: (value: string) => {
                    return <Tag color="blue">{value}</Tag>;
                },
            },
            ...(_variants ?? []).map((exp_value) => {
                return {
                    title: getExpAliasById(exp_value),
                    dataIndex: [exp_value, "rate"],
                    key: [exp_value, "rate"].join(),
                    width: 200,
                    render: (value: number, record: any) => {
                        return (
                            <CellRenderWithSignificant
                                compareRate={value ?? 0}
                                originRate={record[exp_value]?.["originRate"] ?? 0}
                                eventLists={applyValue.eventLists ?? []}
                                exp_value={exp_value}
                                getExpAliasById={getExpAliasById}
                                record={record}
                                significantInfo={
                                    significantInfoAllStep?.[record.step]?.[breakdownValue]?.["variants"]?.[exp_value]
                                }
                                significanceLevel={significanceLevel}
                                isBaseVariant={exp_value === baseVariant}
                                mode={mode}
                            />
                        );
                    },
                };
            }),
        ];
    };

    if (!applyValue.breakdownBy) {
        return (
            <div
                css={css`
                    .ant-table-cell {
                        &:has(.custom-cell) {
                            padding: 0 !important;
                        }
                    }
                `}
            >
                <Table
                    rowKey={"step"}
                    bordered={true}
                    loading={isLoading}
                    columns={columns("-")}
                    dataSource={dataSource}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                />
            </div>
        );
    }
    const breakdownBy = applyValue.breakdownBy;
    const dataSourceParent = (applyValue.breakdownValues ?? []).map((v) => {
        return {
            [breakdownBy]: v,
            expandTableRecords: dataSource.map((data: Record<string, any>, index) => {
                return {
                    step: data.step,
                    index: index,
                    ...(data[v] ?? {}),
                };
            }),
        };
    });

    const columnsParent: ColumnType<any>[] = [
        {
            title: toTitle(applyValue.breakdownBy),
            dataIndex: breakdownBy,
            key: breakdownBy,
            fixed: "left",
            sorter: (a, b) => {
                return a[breakdownBy].localeCompare(b[breakdownBy]);
            },
            render: (value: string) => <span className="table-group-custom font-bold">{value}</span>,
        },
    ];

    return (
        <Table
            rowKey={breakdownBy}
            bordered={true}
            loading={isLoading}
            columns={columnsParent}
            dataSource={dataSourceParent}
            scroll={{ x: "max-content", y: 800 }}
            pagination={false}
            expandable={{
                expandedRowRender: (record: Record<string, any>) => {
                    return (
                        <div
                            css={css`
                                .ant-table-cell {
                                    &:has(.custom-cell) {
                                        padding: 0 !important;
                                    }
                                }
                            `}
                        >
                            <Table
                                rowKey={"step"}
                                bordered={true}
                                loading={isLoading}
                                columns={columns(record[breakdownBy])}
                                dataSource={record["expandTableRecords"]}
                                scroll={{ x: "max-content" }}
                                pagination={false}
                            />
                        </div>
                    );
                },
                defaultExpandedRowKeys: applyValue.breakdownValues ?? [],
            }}
        />
    );
};
export const FunnelTable = React.memo(FunnelTableComponent);
